import { createSelector, createSlice } from '@reduxjs/toolkit'
import {
  baseReducers,
  getIdOrModelId,
  SBAPI,
  SBSelectRaw,
  SBState,
} from '@supplement-bacon/react-toolkit/utils'
import { initialQuery } from '@supplement-bacon/sb-table-filters'
import { WhatsappBotSettingSchema } from '../models/schema'
import {
  WhatsappBotSetting,
  WhatsappBotSettingKey,
} from '../models/WhatsappBotSetting'
import {
  fetchWhatsappBotSettingById,
  fetchWhatsappBotSettings,
} from '../services/WhatsappBotSettingService'
import { AppDispatch, RootState } from './store'

const initialState: SBState<WhatsappBotSetting> = {
  isLoading: false,
  error: null,
  items: {},
  ids: [],
  selectedId: undefined,
  query: initialQuery,
}

const slice = createSlice({
  name: 'whatsappBotSetting',
  initialState,
  reducers: baseReducers,
})

// Reducer
export default slice.reducer
export const {
  getItemsSuccess: getWhatsappBotSettingsSuccess,
  setQuery: setWhatsappBotSettingQuery,
  reset: resetWhatsappBotSettingState,
  resetQueryAndIds: resetWhatsappBotSettingQueryAndIds,
} = slice.actions

/**
 * Selectors
 */

const selectRawItems: SBSelectRaw<
  RootState,
  { [key: number]: WhatsappBotSetting }
> = (state: RootState) => state[slice.name].items
const selectRawIds: SBSelectRaw<RootState, number[]> = (state: RootState) =>
  state[slice.name].ids
const selectRawSelectedId: SBSelectRaw<RootState, number | undefined> = (
  state: RootState
) => state[slice.name].selectedId

export const selectWhatsappBotSettings = () =>
  createSelector(
    [selectRawItems, selectRawIds],
    (items, ids) => ids.map((id) => items[id]).filter((i) => i)
    // Filter allow to return only non-null elements
  )
export const selectSelectedWhatsappBotSetting = () =>
  createSelector([selectRawItems, selectRawSelectedId], (items, id) =>
    id !== undefined ? items[id] : undefined
  )
export const selectWhatsappBotSettingById = (id: number) =>
  createSelector([selectRawItems], (items) =>
    items.hasOwnProperty(id) ? items[id] : undefined
  )
export const selectWhatsappBotWhatsappBotSettingsByIds = (ids: number[]) =>
  createSelector([selectRawItems], (items) =>
    ids.filter((id) => items.hasOwnProperty(id)).map((id) => items[id])
  )
export const selectWhatsappBotSettingByKey = (key: string) =>
  createSelector([selectRawItems], (items) =>
    Object.entries(items)
      .map((i) => i[1])
      .find((whatsappBotSetting) => whatsappBotSetting.key === key)
  )

/**
 * Actions
 */

export const setSelectedWhatsappBotSetting =
  (whatsappBotSetting: WhatsappBotSetting | number) =>
  async (dispatch: AppDispatch) =>
    dispatch(
      slice.actions.setSelectedId(
        getIdOrModelId<WhatsappBotSetting>(whatsappBotSetting)
      )
    )

export const getWhatsappBotSettings = (
  whatsappBotSettings: WhatsappBotSettingKey[],
  venueId?: number
) =>
  SBAPI.FetchPaginatedDispatched(
    fetchWhatsappBotSettings(whatsappBotSettings, venueId),
    [WhatsappBotSettingSchema],
    slice.actions
  )

export const getWhatsappBotSettingWithId = (id: number) =>
  SBAPI.FetchDispatched<WhatsappBotSetting>(
    fetchWhatsappBotSettingById(id),
    WhatsappBotSettingSchema,
    slice.actions
  )
